import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Check, X, Trash2 } from "lucide-react";

/** Admin review of agent applications. */
export default function AgentApplicationsAdmin() {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/admin/agent-applications")).data.applications || []);
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    const note = prompt(`Optional note for ${decision}:`) || "";
    try { await api.post(`/admin/agent-applications/${id}/decision`, { decision, note }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this application?")) return;
    await api.delete(`/admin/agent-applications/${id}`); await load();
  };

  const badge = (s) => {
    const st = { pending: { bg: "rgba(212,175,55,0.16)", c: "#8a6b12" },
                 approved: { bg: "rgba(37,211,102,0.14)", c: "#0A7A2C" },
                 rejected: { bg: "rgba(220,53,69,0.12)", c: "#a11d29" } }[s || "pending"];
    return <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.c }}>{s || "pending"}</span>;
  };

  return (
    <div className="mv-card divide-y mv-hairline" data-testid="admin-agent-applications">
      {items.map((a) => (
        <div key={a.id} className="p-5 flex flex-wrap justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold mv-green-text">{a.full_name}</div>
              {badge(a.status)}
            </div>
            <div className="text-[13px] mt-0.5" style={{ color: "var(--mv-muted)" }}>
              {a.country_current} → serves {a.country_serving || "—"} · {a.phone || a.email || "no contact"} · languages: {a.languages || "—"}
            </div>
            {a.experience && <div className="text-[13px] mt-2"><b>Experience:</b> {a.experience}</div>}
            {a.why_join && <div className="text-[13px] mt-1"><b>Motivation:</b> {a.why_join}</div>}
            {a.agent_code && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ background: "rgba(37,211,102,0.14)" }}>
                <span className="text-[10.5px] uppercase tracking-[0.18em]" style={{ color: "#0A7A2C" }}>Agent code</span>
                <b className="mv-green-text font-display" data-testid={`agent-code-${a.id}`}>{a.agent_code}</b>
              </div>
            )}
            {a.admin_note && <div className="text-[12.5px] mt-2 italic" style={{ color: "#a11d29" }}>Admin note: {a.admin_note}</div>}
            <div className="text-[11px] mt-2" style={{ color: "var(--mv-muted)" }}>{(a.created_at || "").slice(0,10)}</div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {a.status === "pending" && <>
              <button onClick={() => decide(a.id, "approved")} className="btn-green !py-1.5 !px-3" data-testid={`agent-approve-${a.id}`}><Check size={14}/> Approve</button>
              <button onClick={() => decide(a.id, "rejected")} className="btn-ghost !py-1.5 !px-3" data-testid={`agent-reject-${a.id}`}><X size={14}/> Reject</button>
            </>}
            <button onClick={() => remove(a.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl self-end"><Trash2 size={16}/></button>
          </div>
        </div>
      ))}
      {!items.length && <div className="p-6 text-neutral-500 text-[13.5px]">No agent applications yet. Share the /agent-portal link.</div>}
    </div>
  );
}
